import React, { useMemo, useState } from 'react';
import { getISOWeek, SOMMAR_VECKOR, vinterVeckor } from '../utils';
import { mergeSeasons } from './paceMerge';

const ACCENT = '#1d6a5f';
const parseISO = (s) => new Date(s + 'T12:00:00Z');
const iso = (d) => d.toISOString().slice(0, 10);
function isoMonday(y, w) {
  const jan4 = new Date(Date.UTC(y, 0, 4));
  const dow = (jan4.getUTCDay() + 6) % 7;
  const wk1 = new Date(jan4); wk1.setUTCDate(jan4.getUTCDate() - dow);
  const d = new Date(wk1); d.setUTCDate(wk1.getUTCDate() + (w - 1) * 7);
  return d;
}
function histAt(PH, sid, asOf) {
  const m = {}; const ps = PH[sid]; if (!ps) return m;
  for (const r of ps.rows) if (r.asOf === asOf && r.bel != null) m[r.week] = { bel: r.bel / 100, gn: r.gastn, rumsn: r.rumsn };
  return m;
}
// fjolårets avstämning vars ISO-vecka ligger närmast N (±4)
function priorAtAvst(PH, sid, N) {
  const ps = PH[sid]; if (!ps) return {};
  let best = null, bd = 99;
  for (const s of ps.snapshots || []) { const w = getISOWeek(s); let d = Math.abs(w - N); d = Math.min(d, 53 - d); if (d < bd) { bd = d; best = s; } }
  return (best && bd <= 4) ? histAt(PH, sid, best) : {};
}

const TROSKEL_PP = 10; // släpar = minst så här många pp efter fjolåret

export default function AktionsLista({ byDatum, totalRooms = 45, paceLive }) {
  const flags = useMemo(() => {
    const PH = mergeSeasons(paceLive);
    const now = new Date(); const curW = getISOWeek(now);
    const y = now.getUTCFullYear(), mo = now.getUTCMonth();
    const sommarY = mo >= 10 ? y + 1 : y;
    const vinterY = mo >= 4 ? y : y - 1;
    const yy = (n) => String(n).slice(2);
    const scan = [
      { weeks: SOMMAR_VECKOR, yearOf: () => sommarY, prior: `sommar-${sommarY - 1}` },
      { weeks: vinterVeckor(vinterY), yearOf: (w) => w >= 40 ? vinterY : vinterY + 1, prior: `vinter-${yy(vinterY - 1)}${yy(vinterY)}` },
    ];
    const out = [];
    for (const sc of scan) {
      const fjolMap = priorAtAvst(PH, sc.prior, curW);
      for (const w of sc.weeks) {
        const mon = isoMonday(sc.yearOf(w), w);
        if (mon <= now) continue;                       // bara framtid (går att agera på)
        let rumsn = 0;
        for (let i = 0; i < 7; i++) { const d = new Date(mon); d.setUTCDate(mon.getUTCDate() + i); const rec = byDatum[iso(d)]; if (rec) rumsn += rec.rumRumO; }
        const iBel = rumsn / (totalRooms * 7);
        if (iBel >= 0.9) continue;                       // redan starkt bokad
        const f = fjolMap[w]; if (!f || f.bel == null) continue;
        const dpp = Math.round((iBel - f.bel) * 100);
        if (dpp > -TROSKEL_PP) continue;                 // i fas eller före → ingen flagga
        out.push({ w, mon, iBel, fjBel: f.bel, dpp, behind: Math.round((f.bel - iBel) * totalRooms * 7), leadW: Math.round((mon - now) / (7 * 86400000)) });
      }
    }
    return out.sort((a, b) => a.mon - b.mon);
  }, [byDatum, paceLive, totalRooms]);

  const dStr = (d) => `${d.getUTCDate()}/${d.getUTCMonth() + 1}`;

  // Dölj-läge: persistent tills läget ändras (ny signatur → puffen kommer tillbaka)
  const KEY = 'belaggning_aktion_dismissed';
  const sig = useMemo(() => flags.length ? 'w:' + flags.map(f => `${f.w}:${f.dpp}`).join(',') : 'green', [flags]);
  const [dismissedSig, setDismissedSig] = useState(() => { try { return localStorage.getItem(KEY); } catch (_) { return null; } });
  const [forceShow, setForceShow] = useState(false);
  const dismissed = !forceShow && dismissedSig === sig;
  const dismiss = () => { try { localStorage.setItem(KEY, sig); } catch (_) {} setDismissedSig(sig); setForceShow(false); };
  const reopen = () => setForceShow(true);
  const xBtn = (
    <button onClick={dismiss} aria-label="Dölj" title="Dölj"
      style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, lineHeight: 1, color: 'var(--text-secondary)', padding: '0 2px' }}>×</button>
  );

  if (dismissed) {
    return (
      <div className="card" style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'system-ui,sans-serif' }}>
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {flags.length ? <><span style={{ color: '#A32D2D' }}>⚠</span> {flags.length} {flags.length === 1 ? 'vecka släpar' : 'veckor släpar'} mot fjolåret</> : <span style={{ color: '#1D9E75' }}>🟢 Inget släpar</span>}
        </span>
        <button onClick={reopen} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: ACCENT }}>Visa</button>
      </div>
    );
  }

  if (!flags.length) {
    return (
      <div className="card" style={{ borderLeft: '3px solid #1D9E75' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'system-ui,sans-serif', fontSize: 13.5, color: 'var(--text-secondary)' }}>
          <span><strong style={{ color: '#1D9E75' }}>🟢 Inget släpar.</strong> Alla kommande veckor är i fas med eller före fjolåret (vid samma ledtid).</span>
          {xBtn}
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ borderLeft: '3px solid #A32D2D' }}>
      <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ color: '#A32D2D' }} aria-hidden="true">⚠</span>
        Att agera på — {flags.length} {flags.length === 1 ? 'vecka släpar' : 'veckor släpar'} mot fjolåret
        {xBtn}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontFamily: 'system-ui,sans-serif' }}>
        {flags.slice(0, 10).map(f => (
          <div key={f.w} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '8px 12px', background: '#FCEBEB', borderRadius: 8 }}>
            <span style={{ fontWeight: 700, minWidth: 96 }}>v.{f.w} <span style={{ color: 'var(--text-secondary)', fontWeight: 400, fontSize: 12 }}>{dStr(f.mon)}</span></span>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>om {f.leadW} v</span>
            <span style={{ fontSize: 13 }}>i år <strong>{Math.round(f.iBel * 100)}%</strong> · ifjol {Math.round(f.fjBel * 100)}%</span>
            <span style={{ fontWeight: 700, color: '#A32D2D' }}>{f.dpp} pp</span>
            <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginLeft: 'auto' }}>~{f.behind} rumsnätter efter</span>
          </div>
        ))}
        {flags.length > 10 && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>+{flags.length - 10} fler veckor släpar</div>}
      </div>
      <div className="note" style={{ marginTop: 8 }}>Kommande veckor som ligger ≥{TROSKEL_PP} pp efter fjolåret vid samma ledtid (inkl. offert) — kandidater för kampanj/pris. Källa: live + pace-historik.</div>
    </div>
  );
}
